/**
 * JobsTable — Reusable history table for all three modules.
 *
 * Props:
 *   client     — { listJobs, cancelJob }
 *   downloads  — Array of { label, getUrl } shown for completed jobs
 *   onCompare  — Optional: called with job_id when "Compare" is clicked (reports only)
 */
import { useJobsTable, type JobTableClient } from '@/hooks/useJobsTable'
import type { HistoryJob } from '@/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const secs = (Date.now() - new Date(iso).getTime()) / 1000
  if (secs < 10)    return 'just now'
  if (secs < 60)    return `${Math.floor(secs)}s ago`
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return `${Math.floor(secs / 86400)}d ago`
}

const STATUS: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  queued:   { bg: 'bg-slate-100',   text: 'text-slate-600',   dot: 'bg-slate-400',   label: 'Queued'   },
  running:  { bg: 'bg-blue-50',     text: 'text-blue-700',    dot: 'bg-blue-500',    label: 'Running'  },
  complete: { bg: 'bg-emerald-50',  text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Complete' },
  failed:   { bg: 'bg-red-50',      text: 'text-red-700',     dot: 'bg-red-400',     label: 'Failed'   },
}

function StatusBadge({ status, progress }: { status: string; progress: number }) {
  const cfg = STATUS[status] ?? STATUS.queued
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {status === 'running' ? (
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
      ) : (
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      )}
      {cfg.label}
      {status === 'running' && progress > 0 && (
        <span className="ml-0.5 opacity-70">{progress}%</span>
      )}
    </span>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M2 3.5h9M4.5 3.5V2.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5.5 6v3.5M7.5 6v3.5M3 3.5l.5 7a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l.5-7"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M6 2v6M3.5 6l2.5 2.5L8.5 6M1.5 10.5h9"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function CompareIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M2 6.5h9M7 4l2.5 2.5L7 9M6 4 3.5 6.5 6 9"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function EyeIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M1.5 6.5C1.5 6.5 3.5 2.5 6.5 2.5s5 4 5 4-2 4-5 4-5-4-5-4z"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="6.5" cy="6.5" r="1.5" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  )
}

// ── Row ───────────────────────────────────────────────────────────────────────

interface DownloadLink { label: string; getUrl: (id: string) => string }

interface RowProps {
  job:       HistoryJob
  downloads: DownloadLink[]
  onDelete:  (id: string) => void
  onCompare?: (id: string) => void
  onOpen?:   (id: string) => void
}

function JobRow({ job, downloads, onDelete, onCompare, onOpen }: RowProps) {
  return (
    <tr
      className={`group border-b border-slate-50 transition-colors ${
        onOpen && job.status === 'complete'
          ? 'hover:bg-blue-50/40 cursor-pointer'
          : 'hover:bg-slate-50/60'
      }`}
      onClick={onOpen && job.status === 'complete' ? () => onOpen(job.job_id) : undefined}
    >
      {/* Label */}
      <td className="py-3 pl-5 pr-3 max-w-[200px]">
        <span
          className="text-sm text-slate-700 font-medium truncate block"
          title={job.label || job.job_id}
        >
          {job.label || <span className="text-slate-400 font-normal">—</span>}
        </span>
        <span className="text-xs text-slate-400 font-mono truncate block" title={job.job_id}>
          {job.job_id.slice(0, 8)}…
        </span>
      </td>

      {/* Status */}
      <td className="py-3 px-3 whitespace-nowrap">
        <StatusBadge status={job.status} progress={job.progress} />
        {job.step && job.status === 'running' && (
          <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[130px]">{job.step}</p>
        )}
        {job.error && (
          <p className="text-xs text-red-400 mt-0.5 truncate max-w-[130px]" title={job.error}>
            {job.error}
          </p>
        )}
      </td>

      {/* Created */}
      <td className="py-3 px-3 whitespace-nowrap">
        <span className="text-xs text-slate-500" title={job.created_at}>
          {relativeTime(job.created_at)}
        </span>
      </td>

      {/* Progress bar (running only) */}
      <td className="py-3 px-3 w-28">
        {job.status === 'running' && (
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-400 rounded-full transition-all duration-700"
              style={{ width: `${job.progress}%` }}
            />
          </div>
        )}
      </td>

      {/* Actions */}
      <td className="py-3 pl-3 pr-5">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* View report (complete + onOpen provided) */}
          {onOpen && job.status === 'complete' && (
            <button
              onClick={e => { e.stopPropagation(); onOpen(job.job_id) }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-blue-200
                         text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-800
                         transition-colors font-medium"
            >
              <EyeIcon /> View
            </button>
          )}

          {/* Downloads (complete only) */}
          {job.status === 'complete' && downloads.map(dl => (
            <a
              key={dl.label}
              href={dl.getUrl(job.job_id)}
              download
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200
                         text-xs text-slate-600 hover:bg-slate-100 hover:text-slate-800
                         transition-colors font-medium"
            >
              <DownloadIcon /> {dl.label}
            </a>
          ))}

          {/* Compare (reports only, complete only) */}
          {onCompare && job.status === 'complete' && (
            <button
              onClick={() => onCompare(job.job_id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-violet-200
                         text-xs text-violet-600 hover:bg-violet-50 hover:text-violet-800
                         transition-colors font-medium"
            >
              <CompareIcon /> Compare
            </button>
          )}

          {/* Cancel (queued / running) */}
          {(job.status === 'queued' || job.status === 'running') && (
            <button
              onClick={() => onDelete(job.job_id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200
                         text-xs text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200
                         transition-colors font-medium"
            >
              Cancel
            </button>
          )}

          {/* Delete (complete / failed) */}
          {(job.status === 'complete' || job.status === 'failed') && (
            <button
              onClick={() => onDelete(job.job_id)}
              title="Remove from history"
              className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-200
                         text-slate-400 hover:bg-red-50 hover:text-red-500 hover:border-red-200
                         transition-colors opacity-0 group-hover:opacity-100"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── Table ─────────────────────────────────────────────────────────────────────

interface JobsTableProps {
  client:    JobTableClient
  downloads: DownloadLink[]
  onCompare?: (jobId: string) => void
  onOpen?:   (jobId: string) => void
}

export default function JobsTable({ client, downloads, onCompare, onOpen }: JobsTableProps) {
  const { jobs, loading, error, refresh, deleteJob } = useJobsTable(client)

  return (
    <div className="max-w-6xl mx-auto px-5 pt-6 pb-16">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-700">Job History</h2>
        <button
          onClick={refresh}
          className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1"
          title="Refresh"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M1.5 6A4.5 4.5 0 1 1 5 10.5M1.5 4v2.5H4"
                  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-slate-100 overflow-hidden">
        {loading && (
          <div className="py-16 text-center text-sm text-slate-400">Loading history…</div>
        )}

        {!loading && error && (
          <div className="py-16 text-center text-sm text-red-400">{error}</div>
        )}

        {!loading && !error && jobs.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-sm text-slate-400">No jobs yet.</p>
            <p className="text-xs text-slate-300 mt-1">Create your first job to see it here.</p>
          </div>
        )}

        {!loading && !error && jobs.length > 0 && (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="py-2.5 pl-5 pr-3 text-xs font-medium text-slate-400 uppercase tracking-wide">Label</th>
                <th className="py-2.5 px-3 text-xs font-medium text-slate-400 uppercase tracking-wide">Status</th>
                <th className="py-2.5 px-3 text-xs font-medium text-slate-400 uppercase tracking-wide">Created</th>
                <th className="py-2.5 px-3 text-xs font-medium text-slate-400 uppercase tracking-wide w-28">Progress</th>
                <th className="py-2.5 pl-3 pr-5 text-xs font-medium text-slate-400 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <JobRow
                  key={job.job_id}
                  job={job}
                  downloads={downloads}
                  onDelete={deleteJob}
                  onCompare={onCompare}
                  onOpen={onOpen}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
